import { useEffect, useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { getQualificationClassName } from 'src/utils/humanize';
import useBadgesQualifications from 'src/hooks/useBadgesQualifications';
import { Assertion, Qualification } from 'src/types';
import env from 'src/env/index';
import { useNavigate } from 'react-router-dom';
import QualificationContainer from './QualificationContainer';
import QualificationBadges from './QualificationBadges';
import ErrorContainer from '../Error/ErrorContainer';
import QualificationContainerSkeleton from './QualificationContainerSkeleton';

const Qualifications = () => {
    const navigate = useNavigate();
    const intl = useIntl();
    const pracId = localStorage.getItem('active_practitioner_id');
    const [qualificationsResponse, setQualificationsResponse] = useState<Array<Qualification>>([]);
    const [badgesResponse, setBadgesResponse] = useState<Array<Assertion>>([]);
    const qualificationNames: string[] = [
        'lumina_spark', 'lumina_emotion', 'lumina_select', 'lumina_sales', 'lumina_leader', 'manager_as_coach',
    ];
    const practitionerQualifications: string[] = [];

    const {
        qualifications,
        qualificationsError,
        qualificationsLoading,
        badges,
        badgesLoading,
        badgesError,
    } = useBadgesQualifications();

    // returns qualifications that are yet to be qualified by the practitioner
    // eslint-disable-next-line
    const notPresentQualifications = () => qualificationNames.filter((val) => !practitionerQualifications.includes(val));

    useEffect(() => {
        if (badges) {
            setBadgesResponse(badges.practitionerProfileBadges);
        }
        if (qualifications) {
            setQualificationsResponse(qualifications.practitionerProfileBadgesQualifications);
        }
    }, [badges, qualifications]);

    return (
        <div className='mt-16 flex flex-col'>
            <span className='text-headline-reg font-bold flex justify-center xl:justify-start'>
                {
                    intl.formatMessage({
                        id: 'LP_LUMINA_QUALIFICATIONS',
                        defaultMessage: 'Your Qualifications',
                    })
                }
            </span>
            <div className='flex flex-row space-x-6 justify-center xl:justify-start'>
                <button onClick={() => { navigate('/products'); }}
                    className='flex flex-row place-items-center hover:underline gap-x-2 h-8 '>
                    <span className='font-bold'>
                        {
                            intl.formatMessage({
                                id: 'LP_SEE_PRODUCTS',
                                defaultMessage: 'See all Products',
                            })
                        }
                    </span>
                    <ChevronRightIcon className='text-gradients-primary-start h-3.5' />
                </button>
                <a href={`${env.joinQualificationUrl}`} target='_black'
                    className='flex flex-row place-items-center hover:underline text-label-reg gap-x-2 h-8 '>
                    <span className='font-bold'>
                        {
                            intl.formatMessage({
                                id: 'LP_JOIN_QUALIFICATION',
                                defaultMessage: 'Join a Qualification',
                            })
                        }
                    </span>
                    <ChevronRightIcon className='text-gradients-primary-start h-3.5' />
                </a>
            </div>

            {(qualificationsLoading || badgesLoading)
                && <div className='flex flex-wrap md:flex-row mt-6 justify-center xl:justify-start'>
                    {qualificationNames.map((_qualificationName, index) => (
                        <QualificationContainerSkeleton key={index} />
                    ))}
                </div>

            }

            {pracId && qualificationsResponse && qualificationsResponse.length !== 0
                && <div className='flex flex-wrap md:flex-row mt-6  justify-center xl:justify-start'>
                    {qualificationsResponse.map((qualification: {
                        qualificationDate: Date, qualificationName: string
                    }, index: number) => {
                        const qualifiedDate: any = <FormattedDate
                            value={qualification.qualificationDate}
                            day='numeric'
                            month='numeric'
                            year='numeric'
                        />;
                        practitionerQualifications.push(qualification.qualificationName);

                        return (
                            <QualificationContainer
                                key={index}
                                isLoading={false}
                                isQualified={true}
                                practitionerId={Number(pracId)}
                                badgesArray={badgesResponse}
                                qualification={qualification.qualificationName}
                                dateQualified={qualifiedDate}
                                qualificationImage={
                                    <img src={QualificationBadges[qualification.qualificationName]}
                                        alt={QualificationBadges[qualification.qualificationName]} />}
                                className={
                                    `text-product-${getQualificationClassName(qualification.qualificationName)}-regular`
                                }
                            />
                        );
                    })}

                    {notPresentQualifications()
                        .filter((qualification: string) => qualification !== 'manager_as_coach')
                        .map((qualification: string, index: number) => (
                            <QualificationContainer
                                key={index}
                                isLoading={false}
                                isQualified={false}
                                badgesArray={[]}
                                qualification={qualification}
                                dateQualified=''
                                qualificationImage={
                                    <img src={QualificationBadges[qualification]}
                                        alt={QualificationBadges[qualification]} className='opacity-40' />}
                                className={`text-product-${getQualificationClassName(qualification)}-regular`}
                            />
                        ))
                    }
                </div>
            }
            {
                badgesError
                && <div className='mt-4'><ErrorContainer errorMessage={badgesError.message} /></div>
            }
            {
                qualificationsError
                && <div className='mt-4'><ErrorContainer errorMessage={qualificationsError.message} /></div>
            }
        </div >
    );
};

export default Qualifications;